'use client'

import { Fragment, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowBigUp, ArrowBigDown, MessageSquare, Share2, Plus, Search, Eye, Pin, HelpCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { settings } from '../../../config/settings';
import { apiGetMe } from '../../helpers/auth';
import RoleBadge from '../../components/RoleBadge';
import { useTranslations } from 'next-intl';

export default function ForumsPage() {
  const t = useTranslations('Auto.Forums');
  const router = useRouter();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [me, setMe] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 0 });
  const forumTabs = [
    { value: 'home', label: t('home') },
    ...settings.forum_categories.map((category) => ({ value: category.id, label: category.name })),
  ];
  const activeTabIndex = Math.max(0, forumTabs.findIndex((tab) => tab.value === activeTab));

  useEffect(() => {
    apiGetMe()
      .then((data) => setMe(data?.user ?? null))
      .catch(() => setMe(null));
  }, []);

  useEffect(() => {
    loadPosts();
  }, [activeTab, pagination.page]);

  async function loadPosts() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        category: activeTab,
        page: pagination.page.toString(),
        limit: '20'
      } as Record<string, string>);

      const res = await fetch(`/api/forums/posts?${params}`);
      const data = await res.json();

      if (res.ok) {
        setPosts(data.posts);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleVote(postId, voteType) {
    if (!me) return;

    try {
      const res = await fetch('/api/forums/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          target_type: 'post',
          target_id: postId,
          vote_type: voteType
        })
      });

      if (res.ok) {
        const voteData = await res.json();
        setPosts(posts.map(post => 
          post.id === postId 
            ? { ...post, ...voteData, userVote: voteData.userVote }
            : post
        ));
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  }

  function handleShare(post) {
    const url = `${window.location.origin}/forums/${post.slug}`;
    navigator.clipboard.writeText(url);
  }

  function formatTimeAgo(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = Number(now) - Number(date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  }

  function getDisplayName(user) {
    return user.display_name || user.username;
  }

  return (
      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
      <div className="space-y-4">
        {/* Header */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-foreground">{t('forums')}</h1>
                <Separator orientation="vertical" className="h-5" />
                <p className="text-sm text-muted-foreground">{t('communityDiscussionsSupport')}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder={t('searchPosts')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9 w-[200px] rounded-md border border-input bg-background pl-8 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
                {me && (
                  <Link href="/forums/create">
                    <Button size="sm" className="gap-1 rounded-xl">
                      <Plus className="h-4 w-4" />
                      {t('newPost')}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList
            className="relative grid w-full gap-1 overflow-hidden rounded-2xl bg-muted p-1"
            style={{ gridTemplateColumns: `repeat(${forumTabs.length}, minmax(0, 1fr))` }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-1 left-1 rounded-xl border border-border/60 bg-background shadow-sm transition-transform duration-300 ease-out"
              style={{
                width: `calc((100% - 0.5rem) / ${forumTabs.length})`,
                transform: `translateX(${activeTabIndex * 100}%)`,
              }}
            />
            {forumTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="relative z-10 h-9 rounded-xl">
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Posts Feed */}
        <div className="space-y-3">
          {/* Pinned FAQ notice */}
          <Card
            className="cursor-pointer border-primary/40 bg-primary/5 transition-colors hover:border-primary/70"
            onClick={() => router.push('/faq')}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 shrink-0">
                    <HelpCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Pin className="h-3.5 w-3.5 text-primary" />
                    <h3 className="text-base font-bold text-foreground">Before posting, check our FAQ &mdash; your question might already be answered</h3>
                  </div>
                </div>
                <Badge variant="secondary" className="shrink-0">{t('pinned')}</Badge>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <div className="animate-pulse space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-muted" />
                      <div className="space-y-1.5">
                        <div className="h-4 w-40 rounded bg-muted" />
                        <div className="h-3 w-24 rounded bg-muted" />
                      </div>
                    </div>
                    <div className="h-5 w-3/4 rounded bg-muted" />
                    <div className="h-10 w-full rounded bg-muted" />
                    <div className="flex gap-4">
                      <div className="h-6 w-20 rounded bg-muted" />
                      <div className="h-6 w-12 rounded bg-muted" />
                      <div className="h-6 w-12 rounded bg-muted" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : posts.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-muted-foreground text-lg">
                  No posts in this category yet. {me ? t('beTheFirstTo') : t('signInToCreate')}
                </p>
              </CardContent>
            </Card>
          ) : (
            posts.map((post, index) => (
              <Fragment key={post.id}>
                <Card
                  className="cursor-pointer transition-colors hover:border-primary/50"
                  onClick={() => router.push(`/forums/${post.slug}`)}
                >
                  <CardContent className="p-4">
                    {/* Post header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <img
                          src={post.avatar_path || settings.auth?.default_avatar_path}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover"
                        />
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span
                              onClick={(e) => { e.stopPropagation(); router.push(`/users/${post.username}`); }}
                              className="text-sm font-semibold text-foreground hover:underline cursor-pointer"
                            >
                              {getDisplayName(post)}
                            </span>
                            <RoleBadge role={post.role} />
                          </div>
                          <p className="text-xs text-muted-foreground">{formatTimeAgo(post.created_at)}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">
                        {settings.forum_categories.find(cat => cat.id === post.category)?.name}
                      </Badge>
                    </div>

                    {/* Post title */}
                    <h3 className="text-lg font-bold text-foreground mb-1">{post.title}</h3>

                    {/* Post excerpt */}
                    <p
                      className="text-sm text-muted-foreground line-clamp-2 mb-3"
                      dangerouslySetInnerHTML={{
                        __html: post.content.replace(/<[^>]*>/g, '').substring(0, 300) + '...'
                      }}
                    />

                    {/* Post footer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Votes */}
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 ${post.userVote === 'up' ? 'text-primary' : 'text-muted-foreground'}`}
                            onClick={() => handleVote(post.id, 'up')}
                            disabled={!me}
                          >
                            <ArrowBigUp className="h-4 w-4" />
                          </Button>
                          <span className="text-xs font-semibold text-foreground w-6 text-center">{post.score}</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-7 w-7 ${post.userVote === 'down' ? 'text-primary' : 'text-muted-foreground'}`}
                            onClick={() => handleVote(post.id, 'down')}
                            disabled={!me}
                          >
                            <ArrowBigDown className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Comments */}
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MessageSquare className="h-3.5 w-3.5" />
                          <span className="text-xs">{post.reply_count}</span>
                        </div>

                        {/* Views */}
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Eye className="h-3.5 w-3.5" />
                          <span className="text-xs">{post.views || 0}</span>
                        </div>

                        {/* Share */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          onClick={(e) => { e.stopPropagation(); handleShare(post); }}
                        >
                          <Share2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>

                      <span className="text-xs text-muted-foreground">
                        {formatTimeAgo(post.last_reply_at || post.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Fragment>
            ))
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-3 pt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              {t('previous')}
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              {t('next')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

